import type { FormattedError } from '@shared/utils/RequestUtils';
import { Button } from '@shared/components/ui/button';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { ArrowLeft, Link2, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { useAdSpendCampaigns } from '../../hooks';
import type { ProfitabilityAdCampaign } from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';
import { AdSpendImportModal, type AdSpendImportModalRef } from '../interaction/AdSpendImportModal';
import { LinkAdCampaignModal } from '../interaction/LinkAdCampaignModal';

/** Fecha API `YYYY-MM-DD` → texto local sin desfase por zona horaria */
function formatLastSpendDay(isoDate: string | null | undefined, locale: string): string {
  if (isoDate == null || isoDate === '') return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return isoDate;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return '—';
  return new Date(y, mo - 1, d).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function isFormattedError(e: unknown): e is FormattedError {
  return typeof e === 'object' && e !== null && 'message' in e && typeof (e as FormattedError).message === 'string';
}

export const AdSpendPage = () => {
  const { t, i18n } = useTranslation('profitabilityModule');
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'es-ES';
  const navigate = useNavigate();
  const importModalRef = useRef<AdSpendImportModalRef>(null);
  const [linkCampaign, setLinkCampaign] = useState<ProfitabilityAdCampaign | null>(null);
  const currency = useCurrency();

  const campaignsQ = useAdSpendCampaigns(true);

  const errorDescription = (() => {
    const e = campaignsQ.error;
    if (!e || !isFormattedError(e)) return null;
    if (e.statusCode === 403) return e.message || t('errors.forbidden');
    return e.message || t('errors.generic');
  })();

  const hasBlockingError = !!errorDescription && campaignsQ.isError && !campaignsQ.data && !campaignsQ.isLoading;

  const rows = campaignsQ.data?.data ?? [];

  const breadcrumbs = useMemo(
    () => [{ label: t('title'), path: '/reports/profitability' }, { label: t('ad_spend.page_title') }],
    [t],
  );

  const headerActions = useMemo(
    () => [
      {
        id: 'profitability-back-to-report',
        label: t('ad_spend.back_to_overview'),
        onClick: () => navigate('/reports/profitability'),
        icon: <ArrowLeft className="h-4 w-4" />,
        variant: 'outline' as const,
      },
    ],
    [navigate, t],
  );

  usePageNavigation({ breadcrumbs, headerActions });

  return (
    <div className="space-y-6 px-6 py-4" data-testid="profitability-ad-spend-page">
      {hasBlockingError ? (
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.error.title')}
          description={errorDescription ?? t('errors.generic')}
        />
      ) : (
        <>
          <SectionBox data-testid="profitability-ad-spend-import">
            <h2 className="mb-2 text-lg font-semibold text-title">{t('ad_spend.import_title')}</h2>
            <p className="mb-4 text-sm text-muted-foreground">{t('ad_spend.import_hint')}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2 border-border bg-background"
              onClick={() => importModalRef.current?.open()}
              data-testid="profitability-ad-spend-import-trigger"
            >
              <Upload className="h-4 w-4" />
              {t('ad_spend.import_button')}
            </Button>
          </SectionBox>

          <SectionBox data-testid="profitability-ad-spend-campaigns">
            <h2 className="mb-4 text-lg font-semibold text-title">{t('ad_spend.campaigns_title')}</h2>

            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">{t('ad_spend.col_external_id')}</TableHead>
                    <TableHead className="text-muted-foreground">{t('ad_spend.col_name')}</TableHead>
                    <TableHead className="text-muted-foreground">{t('ad_spend.col_product')}</TableHead>
                    <TableHead className="text-muted-foreground">{t('ad_spend.col_pack')}</TableHead>
                    <TableHead className="text-right text-muted-foreground whitespace-nowrap">
                      {t('ad_spend.col_total_spend')}
                    </TableHead>
                    <TableHead className="text-muted-foreground whitespace-nowrap">
                      {t('ad_spend.col_last_spend_on')}
                    </TableHead>
                    <TableHead className="w-[120px] text-muted-foreground">{t('ad_spend.col_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignsQ.isLoading && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        …
                      </TableCell>
                    </TableRow>
                  )}
                  {!campaignsQ.isLoading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        {t('ad_spend.empty_campaigns')}
                      </TableCell>
                    </TableRow>
                  )}
                  {!campaignsQ.isLoading &&
                    rows.map((row) => (
                      <TableRow key={row.id} className="border-border">
                        <TableCell className="max-w-[140px] truncate font-mono text-sm tabular-nums text-foreground">
                          {row.external_id}
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate text-sm text-foreground">
                          {row.name || '—'}
                        </TableCell>
                        <TableCell className="tabular-nums text-sm text-foreground">
                          {row.product_id != null ? `#${row.product_id}` : '—'}
                        </TableCell>
                        <TableCell className="tabular-nums text-sm text-foreground">
                          {row.pack_id != null ? `#${row.pack_id}` : '—'}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-foreground">
                          {formatProfitabilityAmount(row.total_spend ?? null, locale, currency)}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums text-foreground whitespace-nowrap">
                          {formatLastSpendDay(row.last_spend_on ?? null, locale)}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-foreground"
                            onClick={() => setLinkCampaign(row)}
                            data-testid={`profitability-ad-campaign-link-${row.id}`}
                          >
                            <Link2 className="h-4 w-4" />
                            {t('ad_spend.link_action')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </SectionBox>
        </>
      )}

      <AdSpendImportModal ref={importModalRef} />
      <LinkAdCampaignModal
        campaign={linkCampaign}
        open={!!linkCampaign}
        onOpenChange={(o) => !o && setLinkCampaign(null)}
      />
    </div>
  );
};
